import { Button, useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import useApi from "../../shared/hooks/useApi";
import { PlanOfCareDutyId, VisitInstanceId } from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";

type Props = {
  onUndone: () => void;
  planOfCareDutyId: PlanOfCareDutyId;
  visitInstanceId: VisitInstanceId;
  /**
   * WHICH make-up to retract: the one the caller is showing, whether a named
   * make-up in the reassign dialog or an occurrence row in the AngularJS modal.
   * Left out, every make-up of the duty from this visit is retracted.
   */
  targetVisitInstanceId?: VisitInstanceId;
};

/**
 * Undoes a missed duty reassignment from the AngularJS visit instance modal.
 * `visitInstanceId` is the SOURCE visit (the one shown in the modal, which missed the duty) -
 * the same visit the modal already dispatches `openReassignMissedDuty` with.
 */
export default function UndoReassignMissedDutyButton(props: Props) {
  const { mutations } = useApi();
  const toast = useToast();

  const mutation = useMutation({
    ...mutations.planOfCare.undoReassignMissedDuty(props.visitInstanceId),
    onSuccess: () => {
      toast({ title: "Reassignment undone", status: "success", position: "top-right" });
      props.onUndone();
    },
    onError: (error) => {
      toast({
        title: "Error undoing reassignment",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  return (
    <Button
      isLoading={mutation.isPending}
      size="xs"
      variant="link"
      onClick={() =>
        mutation.mutate({
          planOfCareDutyId: props.planOfCareDutyId,
          targetVisitInstanceId: props.targetVisitInstanceId,
        })
      }
    >
      Undo
    </Button>
  );
}
